'use client';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { signIn } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { MicrosoftIcon, GoogleIcon } from '@/components/icons/social-icons';

type Translate = (key: string) => string;

function makeLoginSchema(tAuth: Translate, t: Translate) {
  return z.object({
    email: z.email(tAuth('invalidEmail')),
    password: z.string().min(1, t('passwordRequired')),
  });
}

type LoginFormData = z.infer<ReturnType<typeof makeLoginSchema>>;

/** Error banner messages — keys in the `login` namespace. */
type LoginErrorKey = 'socialLoginFailed' | 'invalidCredentials' | 'genericError';

interface LoginFormProps {
  microsoftEnabled?: boolean;
  googleEnabled?: boolean;
}

export function LoginForm({ microsoftEnabled = false, googleEnabled = false }: LoginFormProps) {
  const t = useTranslations('login');
  const tAuth = useTranslations('auth');
  const loginSchema = useMemo(() => makeLoginSchema(tAuth, t), [tAuth, t]);
  const router = useRouter();
  const searchParams = useSearchParams();
  const rawCallbackUrl = searchParams.get('callbackUrl');
  // Validate the callback URL is same-origin to prevent open-redirect attacks.
  // A URL like "//evil.com" or "/%2F/evil.com" starts with "/" but redirects externally.
  // We use a relative-URL parse trick that works in both browser and SSR (no window needed).
  function isSafeRedirect(url: string | null): url is string {
    if (!url) return false;
    try {
      // Parsing against a known dummy base lets us detect absolute URLs with a host.
      const base = 'https://x';
      const parsed = new URL(url, base);
      // Safe only if the origin matches our dummy base — meaning url had no host of its own.
      return parsed.origin === base;
    } catch {
      return false;
    }
  }
  const callbackUrl = isSafeRedirect(rawCallbackUrl) ? rawCallbackUrl : '/dashboard';
  const [error, setError] = useState<LoginErrorKey | null>(
    searchParams.get('error') === 'social_login_failed' ? 'socialLoginFailed' : null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSocialLoading, setIsSocialLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await signIn('credentials', {
        email: data.email,
        password: data.password,
        redirect: false,
      });

      if (result?.error) {
        setError('invalidCredentials');
      } else {
        router.push(callbackUrl);
      }
    } catch {
      setError('genericError');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = (provider: 'azure-ad' | 'google') => {
    setIsSocialLoading(true);
    setError(null);
    signIn(provider, { callbackUrl });
  };

  const isDisabled = isLoading || isSocialLoading;

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="text-xl">{t('title')}</CardTitle>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          {error && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500">
              {t(error)}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">{tAuth('email')}</Label>
            <Input
              id="email"
              type="email"
              placeholder={tAuth('emailPlaceholder')}
              {...register('email')}
            />
            {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{tAuth('password')}</Label>
            <Input id="password" type="password" {...register('password')} />
            {errors.password && <p className="text-sm text-red-500">{errors.password.message}</p>}
          </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-4">
          <Button type="submit" className="w-full" disabled={isDisabled}>
            {isLoading ? tAuth('signingIn') : t('submit')}
          </Button>

          {(microsoftEnabled || googleEnabled) && (
            <>
              <div className="relative w-full">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">{t('orContinueWith')}</span>
                </div>
              </div>

              <div
                className={`grid gap-3 w-full ${microsoftEnabled && googleEnabled ? 'grid-cols-2' : 'grid-cols-1'}`}
              >
                {microsoftEnabled && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleSocialLogin('azure-ad')}
                    disabled={isDisabled}
                  >
                    <MicrosoftIcon className="mr-2 h-4 w-4" />
                    Microsoft
                  </Button>
                )}
                {googleEnabled && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleSocialLogin('google')}
                    disabled={isDisabled}
                  >
                    <GoogleIcon className="mr-2 h-4 w-4" />
                    Google
                  </Button>
                )}
              </div>
            </>
          )}

          <p className="text-sm text-muted-foreground text-center">
            {t('noAccount')}{' '}
            <Link href="/register" className="text-primary hover:underline">
              {t('signUp')}
            </Link>
          </p>
        </CardFooter>
      </form>
    </Card>
  );
}
